import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTRPCAPI, useTRPCQuery } from '../../../../../../trpc/trpc';
import { AllFilesGroup, groupAllFiles, itemRow, searchWords, songWordOf, uploadRow } from './allFilesModel';

/** The wait after the last key before the search reads the server. */
const SEARCH_DEBOUNCE_MS = 250;
/** One page of each source: the newest files. */
const SEARCH_LIMIT = 96;

/**
 * The rows of the "All files" search (design assets-rethink d4). One word of the query that names a
 * song goes to the server (`assets.list` searches the song title); every word filters the rows here
 * (the song title and the label: "forests story"). The uploads are searched by their file name.
 * The archived post files are in the search too.
 */
export function useAllFilesSearch(input: {
  accountId: number;
  query: string;
  songTitles: string[];
  enabled: boolean;
}): {
  groups: AllFilesGroup[];
  isLoading: boolean;
  error: unknown;
} {
  const { accountId, query, songTitles, enabled } = input;
  const intl = useIntl();
  const trpcAPI = useTRPCAPI();
  const [settled, setSettled] = useState(query);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const words = useMemo(() => searchWords(settled), [settled]);
  const songWord = useMemo(() => songWordOf(words, songTitles), [songTitles, words]);
  const list = useTRPCQuery(
    trpcAPI.assets.list.queryOptions(
      { accountId, query: songWord, includeArchived: true, limit: SEARCH_LIMIT },
      { enabled, retry: false }
    )
  );
  const uploads = useTRPCQuery(
    trpcAPI.assets.uploads.queryOptions({ accountId, limit: SEARCH_LIMIT }, { enabled, retry: false })
  );

  const groups = useMemo(() => {
    const rows = [
      ...(list.data?.items ?? []).map((item) => itemRow(intl, item, list.data?.songs ?? {})),
      ...(uploads.data?.items ?? []).map((upload) => uploadRow(intl, upload)),
    ];
    return groupAllFiles(rows, words);
  }, [intl, list.data, uploads.data, words]);

  return {
    groups,
    isLoading: list.isLoading || uploads.isLoading,
    error: list.error ?? uploads.error ?? undefined,
  };
}
